import React, {createContext, useContext, useMemo} from "react";
import KeycloakService from "../KeycloakService";

export const ROLES = {
  VIEWER: "Viewer",
  ADMIN: "Admin",
  SUPER_ADMIN: "Super-Admin",
} as const;

interface UserRoleContextProps {
  clientRoles: string[];
  /** True for Admin and Super-Admin (and dev mode). Use this to gate edit/write access. */
  isAdmin: boolean;
  /** True only for Super-Admin (and dev mode). Reserved for future Super-Admin-only features. */
  isSuperAdmin: boolean;
  /** True for any role with view access (Admin, Super-Admin, Viewer, and dev mode). Use this to gate viewing access. */
  isViewer: boolean;
}

const UserRoleContext = createContext<UserRoleContextProps>({
  clientRoles: [],
  isAdmin: false,
  isSuperAdmin: false,
  isViewer: false,
});

export const useUserRole = () => useContext(UserRoleContext);

export const UserRoleProvider: React.FC<{ children: React.ReactNode }> = ({children}) => {
  const kcService = KeycloakService.instance;

  const value = useMemo(() => {
    const clientRoles = kcService?.getClientRoles() ?? [];
    const isDev = kcService?.isDev ?? false;
    const isSuperAdmin = isDev || clientRoles.includes(ROLES.SUPER_ADMIN);
    const isAdmin = isSuperAdmin || clientRoles.includes(ROLES.ADMIN);
    const isViewer = isAdmin || clientRoles.includes(ROLES.VIEWER);

    return {clientRoles, isAdmin, isSuperAdmin, isViewer};
  }, [kcService]);

  return (
    <UserRoleContext.Provider value={value}>
      {children}
    </UserRoleContext.Provider>
  );
};
